import React, { useState, useEffect } from "react";
import PodsService from "../../services/podsService";

type Pod = {
    id: string;
    addressTraceId: string;
    podUrl: string;
    createdAt: string;
};

interface PODsModalProps {
    traceId: string;
    isOpen: boolean;
    onClose: () => void;
}

const PODsModal: React.FC<PODsModalProps> = ({ traceId, isOpen, onClose }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pods, setPods] = useState<Pod[]>([]);
    const [selectedPod, setSelectedPod] = useState<Pod | null>(null);

    useEffect(() => {
        if (isOpen && traceId) {
            loadPODs();
        }
    }, [isOpen, traceId]);

    const loadPODs = async () => {
        setLoading(true);
        setError(null);
        setPods([]);
        setSelectedPod(null);

        try {
            const response = await PodsService.getPODsByTraceId(traceId);
            const data = response.data;

            if (Array.isArray(data) && data.length > 0) {
                const sortedData = data.sort(
                    (a: Pod, b: Pod) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                );
                setPods(sortedData);
            } else if (Array.isArray(data) && data.length === 0) {
                setError("No PODs found");
            } else {
                setPods([]);
                setError("Unexpected response format");
            }
        } catch (err: any) {
            console.error("Error fetching PODs:", err);
            if (err.response && err.response.status === 404) {
                setError("No PODs found");
            } else if (err.response && err.response.status === 400) {
                setError("Invalid trace ID");
            } else {
                setError("An error occurred while fetching PODs.");
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex={-1}
            role="dialog"
            onClick={onClose}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content border-0 shadow-lg">
                    <div className="modal-header border-bottom">
                        <h5 className="modal-title">PODs - {traceId}</h5>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={onClose}
                        ></button>
                    </div>

                    <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                        {loading && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 text-muted">Loading PODs...</p>
                            </div>
                        )}

                        {error && !loading && (
                            <div className="alert alert-danger border-0 d-flex align-items-center" role="alert">
                                <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
                                <div>{error}</div>
                            </div>
                        )}

                        {pods.length > 0 && !loading && (
                            <div className="row g-3">
                                {pods.map((pod) => (
                                    <div key={pod.id} className="col-12 col-sm-6 col-md-4">
                                        <div
                                            className="card h-100 border-0 shadow-sm pod-card"
                                            onClick={() => setSelectedPod(pod)}
                                            style={{
                                                cursor: "pointer",
                                                transition: "transform 0.2s",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    paddingBottom: "75%",
                                                    position: "relative",
                                                    overflow: "hidden",
                                                    borderRadius: "4px",
                                                }}
                                            >
                                                <img
                                                    src={pod.podUrl}
                                                    className="card-img-top position-absolute w-100 h-100"
                                                    alt={`POD ${pod.id}`}
                                                    style={{
                                                        objectFit: "cover",
                                                        top: 0,
                                                        left: 0,
                                                    }}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src =
                                                            "https://via.placeholder.com/400x300?text=No+Image";
                                                    }}
                                                />
                                            </div>
                                            <div className="card-body p-2">
                                                <small className="text-muted d-block mb-1">ID: {pod.id}</small>
                                                <small className="text-primary fw-bold">
                                                    {new Date(pod.createdAt).toLocaleString()}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!loading && !error && pods.length === 0 && (
                            <div className="text-center py-5">
                                <i className="bi bi-inbox fs-1 text-muted opacity-50"></i>
                                <p className="mt-2 text-muted">No PODs to display</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* POD Preview Modal */}
            {selectedPod && (
                <div
                    className="modal fade show"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                    tabIndex={-1}
                    role="dialog"
                    onClick={() => setSelectedPod(null)}
                >
                    <div
                        className="modal-dialog modal-xl modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header border-bottom-0">
                                <h5 className="modal-title">POD Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => setSelectedPod(null)}
                                ></button>
                            </div>
                            <div
                                className="modal-body text-center bg-light p-0 d-flex justify-content-center align-items-center"
                                style={{
                                    minHeight: "400px",
                                    backgroundColor: "#f8f9fa",
                                }}
                            >
                                <img
                                    src={selectedPod.podUrl}
                                    alt={`POD ${selectedPod.id}`}
                                    className="img-fluid"
                                    style={{ maxHeight: "70vh" }}
                                />
                            </div>
                            <div className="modal-footer justify-content-between">
                                <div className="text-start">
                                    <div>
                                        <strong>ID:</strong> {selectedPod.id}
                                    </div>
                                    <div>
                                        <strong>Created:</strong>{" "}
                                        {new Date(selectedPod.createdAt).toLocaleString()}
                                    </div>
                                    <div className="text-muted small">Trace: {selectedPod.addressTraceId}</div>
                                </div>
                                <div>
                                    <a
                                        href={selectedPod.podUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-primary me-2"
                                    >
                                        <i className="bi bi-box-arrow-up-right me-2"></i>
                                        Open Original
                                    </a>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setSelectedPod(null)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .pod-card:hover {
                    transform: translateY(-2px);
                }
            `}</style>
        </div>
    );
};

export default PODsModal;
